// Drive state machine for new auto-drive and click-to-accelerate mechanics
export class DriveStateMachine {
    constructor(gameState) {
        this.gameState = gameState;
        this.gameUI = null; // Will be set after GameUI is created

        // Current speed (always >= baseSpeed due to auto-drive)
        this.currentSpeed = 0;

        // Cruise control (timer-based, maintains max speed)
        this.cruiseControlActive = false;
        this.cruiseStartTime = null;
        this.cruiseControlLastUsedTime = null;
        this.cruiseControlAccelerationMultiplier = 2.0; // Multiplier for how fast autopilot ramps up (1.0 = normal acceleration, 2.0 = twice as fast)

        // Overboost - allows clicking beyond max speed
        this.overboostAmount = 0; // Current overboost speed
        this.overboostDecayRate = 3.0; // How fast overboost decays (mph per second)

        // Momentum - idle speed bonus that builds over time
        this.lastInteractionTime = null; // When last click/autopilot occurred
        this.momentumBonus = 0; // Current momentum speed bonus (mph)
        this.lastMomentumCap = null; // Track when momentum cap was last reached (to prevent instant refill on upgrade)
        this._pendingMomentumRestore = null; // Pending momentum to restore on next update
        // Note: momentumRate is now dynamic from gameState.getCurrentMomentumRate()

        // Max speed boost mode - tracks when at max speed for UI effects
        this.maxSpeedBoostActive = false; // Hysteresis flag for max speed state
    }

    setGameUI(gameUI) {
        this.gameUI = gameUI;
    }

    // Helper to get stat calculator
    get statCalculator() {
        return this.gameState.statCalculator;
    }

    // Helper methods to get stats with item effects AND collection bonuses applied
    getCurrentBaseSpeed() {
        const base = this.gameState.getCurrentBaseSpeed();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('baseSpeed', base);
        }
        return base;
    }

    getCurrentAcceleration() {
        const base = this.gameState.getCurrentAcceleration();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('acceleration', base);
        }
        return base;
    }

    getCurrentMaxSpeed() {
        const base = this.gameState.getCurrentMaxSpeed();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('maxSpeed', base);
        }
        return base;
    }

    getCurrentCruiseDuration() {
        const base = this.gameState.getCurrentCruiseDuration();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('cruiseDuration', base);
        }
        return base;
    }

    getCurrentCruiseCooldown() {
        let base = this.gameState.getCurrentCruiseCooldown();
        if (this.statCalculator) {
            base = this.statCalculator.applyAllBonuses('cruiseCooldown', base);
        }
        // Clamp to minimum 5 seconds (matches StatCalculator behavior)
        return Math.max(5, base);
    }

    getCurrentMomentumCap() {
        const base = this.gameState.getCurrentMomentumCap();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('momentum', base);
        }
        return base;
    }

    getCurrentMomentumRate() {
        const base = this.gameState.getCurrentMomentumRate();
        if (this.statCalculator) {
            return this.statCalculator.applyAllBonuses('momentumRate', base);
        }
        return base;
    }

    onDriveButtonClick() {
        // Track if momentum was lost
        const hadMomentum = this.momentumBonus > 0;

        // Reset momentum on any click
        this.lastInteractionTime = performance.now() / 1000;
        this.momentumBonus = 0;

        const accelBoost = this.getCurrentAcceleration();
        const maxSpeed = this.getCurrentMaxSpeed();

        // Use isInBoostMode() which has hysteresis - if button is yellow, clicks should boost
        if (this.isInBoostMode()) {
            // Also push currentSpeed back to max (counteracts drag)
            this.currentSpeed = maxSpeed;

            // Overboost formula: acceleration × 0.5
            // During autopilot, reduce overboost to 50% (accel × 0.25)
            const overboostMultiplier = this.cruiseControlActive ? 0.25 : 0.5;
            const overboostBonus = accelBoost * overboostMultiplier;

            // Add to overboost amount
            this.overboostAmount += overboostBonus;

            // Cap overboost at 50% of acceleration (prevent infinite stacking)
            const maxOverboost = accelBoost * 2;
            this.overboostAmount = Math.min(this.overboostAmount, maxOverboost);

            return { success: true, isOverboost: true, lostMomentum: hadMomentum }; // Click was successful (overboost applied)
        }

        // Normal acceleration (below max speed)
        this.currentSpeed += accelBoost;

        // Clamp to max speed (but don't include overboost yet)
        this.currentSpeed = Math.min(this.currentSpeed, maxSpeed);

        return { success: true, isOverboost: false, lostMomentum: hadMomentum }; // Click was successful (normal acceleration)
    }

    activateCruiseControl(currentTime) {
        if (!this.canActivateCruiseControl(currentTime)) {
            return { activated: false, lostMomentum: false };
        }

        // Track if momentum was lost
        const hadMomentum = this.momentumBonus > 0;

        // Reset momentum on autopilot activation
        this.lastInteractionTime = currentTime;
        this.momentumBonus = 0;

        this.cruiseControlActive = true;
        this.cruiseStartTime = currentTime;
        // Don't set lastUsedTime here - it gets set when cruise ENDS

        return { activated: true, lostMomentum: hadMomentum };
    }

    canActivateCruiseControl(currentTime) {
        if (this.cruiseControlActive) {
            return false; // Already active
        }

        const cooldown = this.getCurrentCruiseCooldown();

        if (this.cruiseControlLastUsedTime === null) {
            return true; // First use
        }

        const timeSinceLastUse = currentTime - this.cruiseControlLastUsedTime;
        const canActivate = timeSinceLastUse >= cooldown;

        return canActivate;
    }

    getCruiseControlCooldownRemaining(currentTime) {
        if (this.cruiseControlLastUsedTime === null) {
            return 0;
        }

        const cooldown = this.getCurrentCruiseCooldown();
        const elapsed = currentTime - this.cruiseControlLastUsedTime;
        const remaining = Math.max(0, cooldown - elapsed);

        return remaining;
    }

    getCruiseControlTimeRemaining(currentTime) {
        if (!this.cruiseControlActive) {
            return 0;
        }

        const duration = this.getCurrentCruiseDuration();
        const elapsed = currentTime - this.cruiseStartTime;
        return Math.max(0, duration - elapsed);
    }

    update(deltaTime, currentTime) {
        const baseSpeed = this.getCurrentBaseSpeed();
        const maxSpeed = this.getCurrentMaxSpeed();
        const aero = this.gameState.getCurrentAerodynamics();

        // Handle pending momentum restoration (from offline simulator or session restore)
        // This is more reliable than manipulating lastInteractionTime
        if (this._pendingMomentumRestore !== null && this._pendingMomentumRestore > 0) {
            const momentumRate = this.getCurrentMomentumRate();
            const maxMomentum = this.getCurrentMomentumCap();

            // Cap the restored momentum to current max
            const restoredMomentum = Math.min(this._pendingMomentumRestore, maxMomentum);
            this.momentumBonus = restoredMomentum;

            // Calculate lastInteractionTime to maintain this momentum going forward
            // Guard against momentumRate being 0
            if (momentumRate > 0) {
                const requiredIdleTime = restoredMomentum / momentumRate;
                this.lastInteractionTime = currentTime - requiredIdleTime;
            } else {
                // If rate is 0, just set interaction time to now (momentum won't grow anyway)
                this.lastInteractionTime = currentTime;
            }

            this.lastMomentumCap = maxMomentum;
            this._pendingMomentumRestore = null;

            // Skip normal momentum calculation this frame since we just restored
        }
        // Calculate momentum bonus (only if idle - not during autopilot)
        // Autopilot and momentum don't stack - momentum only builds when truly idle
        else if (this.cruiseControlActive) {
            // Reset momentum during autopilot
            this.momentumBonus = 0;
            this.lastInteractionTime = currentTime;
            this.lastMomentumCap = null;
        } else if (this.lastInteractionTime !== null) {
            const maxMomentum = this.getCurrentMomentumCap();
            const momentumRate = this.getCurrentMomentumRate();

            // Check if cap increased (player upgraded momentum cap)
            // If so, reset the cap timer to prevent instant fill
            if (this.lastMomentumCap !== null && maxMomentum > this.lastMomentumCap) {
                // Cap was upgraded - start building from current bonus, not from scratch
                // Calculate how long it would take to reach current bonus at current rate
                // Guard against momentumRate being 0
                const timeToCurrentBonus = momentumRate > 0 ? this.momentumBonus / momentumRate : 0;
                this.lastInteractionTime = currentTime - timeToCurrentBonus;
            }
            this.lastMomentumCap = maxMomentum;

            const idleTime = currentTime - this.lastInteractionTime;
            this.momentumBonus = Math.min(idleTime * momentumRate, maxMomentum);
        }

        // Handle cruise control (maintains max speed)
        if (this.cruiseControlActive) {
            const duration = this.getCurrentCruiseDuration();
            const elapsed = currentTime - this.cruiseStartTime;

            if (elapsed >= duration) {
                // Cruise ended - set cooldown start time to NOW (when it ends)
                this.cruiseControlActive = false;
                this.cruiseControlLastUsedTime = currentTime;
                // Play slowdown sound
                if (this.gameUI) {
                    this.gameUI.playSlowdown();
                }
            } else {
                // Ramp up to max speed during cruise (instead of instant max)
                const acceleration = this.getCurrentAcceleration();
                const speedIncrease = acceleration * this.cruiseControlAccelerationMultiplier * deltaTime;
                this.currentSpeed = Math.min(this.currentSpeed + speedIncrease, maxSpeed);
                // Don't return yet - apply overboost below
            }
        }

        // Apply aerodynamic drag (only above base speed, but not during autopilot)
        if (this.currentSpeed > baseSpeed && !this.cruiseControlActive) {
            const excessSpeed = this.currentSpeed - baseSpeed;
            const drag = excessSpeed * aero * deltaTime;
            this.currentSpeed -= drag;
        }

        // Ensure speed doesn't drop below base speed (auto-drive)
        this.currentSpeed = Math.max(baseSpeed, this.currentSpeed);

        // Clamp to max speed (before adding overboost)
        this.currentSpeed = Math.min(this.currentSpeed, maxSpeed);

        // Decay overboost over time
        if (this.overboostAmount > 0) {
            const decay = this.overboostDecayRate * deltaTime;
            this.overboostAmount = Math.max(0, this.overboostAmount - decay);
        }

        // Apply momentum to speed, but cap momentum contribution at max speed
        // (momentum alone cannot push you above max speed)
        const speedWithMomentum = Math.min(this.currentSpeed + this.momentumBonus, maxSpeed);

        // Overboost CAN exceed max speed - it's the reward for active clicking
        const finalSpeed = speedWithMomentum + this.overboostAmount;

        // Convert mph to game units per second
        // World objects are ~4.5x closer than real-world spacing
        // Divide by 4.545 so displayed MPH matches perceived real-world speed
        return finalSpeed * (40 / 60) / 4.545;
    }

    getSpeed() {
        const maxSpeed = this.getCurrentMaxSpeed();
        // Momentum capped at max speed, but overboost can exceed it
        const speedWithMomentum = Math.min(this.currentSpeed + this.momentumBonus, maxSpeed);
        return speedWithMomentum + this.overboostAmount;
    }

    hasOverboost() {
        return this.overboostAmount > 0;
    }

    /**
     * Check if in boost mode (autopilot OR at max speed with hysteresis)
     * Used for drive button styling (yellow color, shake) and boost click behavior
     * Enters at 85% of speed range, exits at 80% (hysteresis prevents flickering)
     */
    isInBoostMode() {
        // Autopilot always counts as boost mode
        if (this.cruiseControlActive) {
            return true;
        }

        const maxSpeed = this.getCurrentMaxSpeed();
        const baseSpeed = this.getCurrentBaseSpeed();

        // Calculate progress through speed range
        const speedRange = maxSpeed - baseSpeed;
        const currentProgress = (this.currentSpeed - baseSpeed) / speedRange;

        // Hysteresis: enter at 85%, exit at 80%
        // This prevents flickering when speed oscillates due to drag
        if (currentProgress >= 0.85) {
            this.maxSpeedBoostActive = true;
        } else if (currentProgress < 0.80) {
            this.maxSpeedBoostActive = false;
        }
        // Between 80-85%: maintain current state (hysteresis)

        return this.maxSpeedBoostActive;
    }

    getSpeedMPH() {
        const maxSpeed = this.getCurrentMaxSpeed();
        // Momentum capped at max speed, but overboost can exceed it
        const speedWithMomentum = Math.min(this.currentSpeed + this.momentumBonus, maxSpeed);
        return speedWithMomentum + this.overboostAmount;
    }

    getMomentumBonus() {
        return this.momentumBonus;
    }

    /**
     * Set momentum to be restored on next update.
     * This is a more reliable method than manipulating lastInteractionTime,
     * which can fail if momentumRate is 0 or due to browser timing differences.
     * @param {number} momentum - The momentum value to restore
     */
    setRestoredMomentum(momentum) {
        if (momentum > 0) {
            this._pendingMomentumRestore = momentum;
        }
    }

    isCruiseControlActive() {
        return this.cruiseControlActive;
    }
}
